/**
 * Reemplaza lib/queries.ts + los pedazos de lib/activityApi.ts y lib/reviewApi.ts
 * que ActivitiesList/ActivityDetail realmente usan. Antes era Supabase
 * (.from()/.rpc(), filtrado por RLS); ahora es fetch() contra el Worker,
 * filtrado por src/authz.ts + src/workflow.ts del backend.
 *
 * institutional_dashboard/unit_dashboard y el ciclo de observaciones
 * (agregar/resolver/decidir revision) ya tienen ruta real en el Worker
 * (dashboards.ts, review.ts) -- reconectados aqui. fetchVersionDiff ya NO es
 * un stub: llama a GET /activities/:id/versions/diff y calcula el diff
 * campo-a-campo del lado del cliente, igual que el RPC original
 * `diff_activity_versions` (ver versioning.ts en el backend).
 */
import type { ExecUnit } from './executive';
import type { ActivityStatus, EngagementStage } from '@rat/shared';
import { apiDownload, apiFetch, apiUpload } from './backend';
import type { RatRole } from '../auth/access';
import type { GateInfo } from './errors';

export interface ActivityRow {
  id: string;
  ref_code: string;
  title: string;
  status: ActivityStatus;
  completeness_pct: number;
  responsible_unit_id: string;
  has_sensitive_data: boolean;
  has_international_transfer: boolean;
  has_automated_decision: boolean;
  updated_at: string;
}

export async function fetchActivities(opts: {
  status?: ActivityStatus;
  search?: string;
  unitId?: string;
  limit?: number;
}): Promise<ActivityRow[]> {
  // Filtrado en el cliente sobre /activities (no /activities?unit=): ese
  // endpoint de compatibilidad del Sprint 1 no hace bypass institucional y
  // solo devuelve {id,title}. /activities ya trae exactamente lo que el
  // usuario puede ver -- filtrar por unidad ahi nunca expone una unidad
  // ajena, porque esa fila ya venia autorizada en la respuesta.
  let rows = await apiFetch<ActivityRow[]>('/activities');
  if (opts.status) rows = rows.filter((r) => r.status === opts.status);
  if (opts.unitId) rows = rows.filter((r) => r.responsible_unit_id === opts.unitId);
  if (opts.search) {
    const s = opts.search.toLowerCase();
    rows = rows.filter((r) => r.title.toLowerCase().includes(s) || r.ref_code.toLowerCase().includes(s));
  }
  return rows.slice(0, opts.limit ?? 50);
}

export interface ActivityDetailResponse extends ActivityRow {
  operational_owner_name: string | null;
  purpose: string | null;
  description: string | null;
  data_source: string | null;
  uses_ai: boolean;
  own_systems_text: string | null;
  retention_value: number | null;
  retention_unit: string | null;
  retention_criterion: string | null;
  retention_text: string | null;
  retention_criterion_label: string | null;
  responsible_unit: { name_short: string } | null;
  data_categories: Array<{ is_sensitive: boolean; data_category: { code: string; label: string } }>;
  subject_categories: Array<{ subject_category: { code: string; label: string } }>;
  legal_bases: Array<{ legal_basis: { code: string; label: string; requires_reinforced: boolean } }>;
  recipients: Array<{
    name: string | null;
    is_processor: boolean;
    detail: string | null;
    recipient_type: { code: string; label: string } | null;
  }>;
  transfers: Array<{ country: string; detail: string | null; guarantee_type: { code: string; label: string } | null }>;
  security_measures: Array<{ detail: string | null; security_measure: { code: string; label: string } }>;
  automated_decisions: Array<{
    description: string;
    stage: string | null;
    human_in_the_loop: boolean;
    produces_profiling: boolean;
  }>;
}

export async function fetchActivity(id: string): Promise<ActivityDetailResponse> {
  return apiFetch<ActivityDetailResponse>(`/activities/${id}`);
}

// ── asistente completo: crear + guardar por paso ───────────────────────────

export async function createActivityApi(input: {
  responsible_unit_id: string;
  title: string;
  operational_owner_name?: string;
}): Promise<string> {
  const result = await apiFetch<{ id: string }>('/activities', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return result.id;
}

/** Campos "core" (no colecciones) -- PATCH generico, solo se mandan los presentes. */
export async function updateActivityCore(id: string, fields: Record<string, unknown>): Promise<void> {
  await apiFetch(`/activities/${id}`, { method: 'PATCH', body: JSON.stringify(fields) });
}

/**
 * "Reemplazar todo" por coleccion -- mismo patron que el asistente viejo
 * (guardar un paso borra y reinserta esa coleccion completa, nunca un add
 * incremental). Cada endpoint recibe `{items: [...]}`.
 */
async function replaceCollectionApi(activityId: string, collection: string, items: unknown[]): Promise<void> {
  await apiFetch(`/activities/${activityId}/${collection}`, { method: 'POST', body: JSON.stringify({ items }) });
}

export const saveDataCategories = (activityId: string, items: Array<{ category: string; is_sensitive: boolean }>) =>
  replaceCollectionApi(activityId, 'data-categories', items);

export const saveLegalBases = (activityId: string, items: Array<{ legal_basis: string; is_reinforced: boolean }>) =>
  replaceCollectionApi(activityId, 'legal-bases', items);

export const saveSubjectCategories = (
  activityId: string,
  items: Array<{ category: string; is_protected_group: boolean }>,
) => replaceCollectionApi(activityId, 'subject-categories', items);

export const saveRecipients = (
  activityId: string,
  items: Array<{ recipient_type: string; name?: string; is_processor: boolean; detail?: string }>,
) => replaceCollectionApi(activityId, 'recipients', items);

export const saveTransfers = (
  activityId: string,
  items: Array<{ country: string; guarantee_type?: string; detail?: string }>,
) => replaceCollectionApi(activityId, 'transfers', items);

export const saveSecurityMeasures = (activityId: string, items: Array<{ measure: string; detail?: string }>) =>
  replaceCollectionApi(activityId, 'security-measures', items);

export const saveAutomatedDecisions = (
  activityId: string,
  items: Array<{ description: string; stage?: string; human_in_the_loop: boolean; produces_profiling: boolean }>,
) => replaceCollectionApi(activityId, 'automated-decisions', items);

export interface VersionSummary {
  id: string;
  version_no: number;
  reason: string;
  created_at: string;
}

export async function fetchActivityHistory(id: string): Promise<VersionSummary[]> {
  const rows = await apiFetch<Array<{ version_no: number; reason: string; created_at: string }>>(
    `/activities/${id}/versions`,
  );
  // el backend no asigna un id propio a cada version -- version_no ya es unico por actividad
  return rows.map((r) => ({ ...r, id: String(r.version_no) })).reverse();
}

// ── ciclo de revision: observaciones + decision del DPD ────────────────────

export type ObservationSeverity = 'obligatoria' | 'sugerida';
/**
 * Ciclo de la observacion (Fase 5): la unidad RESPONDE, el revisor CONFIRMA (resuelve) o reabre.
 *   abierta -> respondida -> resuelta ; resuelta/respondida -> reabierta -> respondida ...
 * `resolved_at` solo se llena cuando el REVISOR la resuelve: una `respondida` sigue contando como abierta para aprobar.
 */
export type ObservationState = 'abierta' | 'respondida' | 'resuelta' | 'reabierta';
export interface Observation {
  id: string;
  field_path: string;
  severity: ObservationSeverity;
  text: string;
  created_at: string;
  resolved_at: string | null;
  state: ObservationState;
}

/** Una entrada del hilo de una observacion (GET /observations/:id/thread). Solo se agregan, nunca se editan. */
export interface ThreadEntry {
  id: string;
  author_email: string | null;
  author_role: string;
  kind: 'comentario' | 'respuesta' | 'resolucion' | 'reapertura';
  body: string;
  created_at: string;
}

export async function fetchOpenObservations(activityId: string): Promise<Observation[]> {
  return apiFetch<Observation[]>(`/activities/${activityId}/observations`);
}

export async function addObservation(
  activityId: string,
  fieldPath: string,
  severity: ObservationSeverity,
  text: string,
): Promise<void> {
  await apiFetch(`/activities/${activityId}/observations`, {
    method: 'POST',
    body: JSON.stringify({ field_path: fieldPath, severity, text }),
  });
}

/**
 * La UNIDAD responde una observacion (texto obligatorio). No la resuelve: eso lo confirma el revisor.
 * Es la accion que desbloquea "marcar como corregida" cuando hay observaciones obligatorias.
 */
export async function respondObservation(observationId: string, body: string): Promise<void> {
  await apiFetch(`/observations/${observationId}/comments`, {
    method: 'POST',
    body: JSON.stringify({ body, action: 'respond' }),
  });
}

export async function fetchObservationThread(observationId: string): Promise<ThreadEntry[]> {
  return apiFetch<ThreadEntry[]>(`/observations/${observationId}/thread`);
}

/** El REVISOR confirma (resolved=true) o reabre (false). Para la unidad la accion es `respondObservation`. */
export async function resolveObservation(observationId: string, resolved: boolean): Promise<void> {
  await apiFetch(`/observations/${observationId}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ resolved }),
  });
}

/**
 * Decision del DPD (aprobar/observar). Deliberadamente distinta de
 * transitionActivity(): pasa por review-decide, que bloquea la aprobacion
 * si quedan observaciones obligatorias sin resolver -- el endpoint generico
 * /status no aplica esa regla.
 */
export async function decideReview(activityId: string, decision: 'approved' | 'observed'): Promise<ActivityStatus> {
  const result = await apiFetch<{ status: ActivityStatus }>(`/activities/${activityId}/review-decide`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
  return result.status;
}

// ── dashboards ──────────────────────────────────────────────────────────

export interface RiskIndicators {
  con_datos_sensibles: number;
  con_transferencia_internacional: number;
  con_decision_automatizada: number;
  observadas_hace_tiempo: number;
}

export interface AttentionRow {
  id: string;
  ref_code: string | null;
  title: string;
  unit_name: string;
  status: string;
  dias_sin_movimiento: number;
}

export interface MonthlyEvolutionRow {
  mes: string;
  creadas: number;
  aprobadas: number;
}

export interface InstitutionalDashboardData {
  unidades_total: number;
  actividades_total: number;
  completitud_institucional: number;
  unidades_sin_actividades: number;
  por_estado: Record<string, number>;
  por_unidad: Array<{
    unit_id: string;
    name: string;
    rat_status: string;
    activities_total: number;
    completeness_avg: number;
  }>;
  riesgo: RiskIndicators;
  atencion: AttentionRow[];
  evolucion_mensual: MonthlyEvolutionRow[];
}

export async function fetchInstitutionalDashboard(): Promise<InstitutionalDashboardData> {
  return apiFetch<InstitutionalDashboardData>('/dashboard/institutional');
}

/** Unidades en las que el usuario puede crear actividades (para el selector de unidad del asistente). */
export async function fetchAssignableUnits(): Promise<Array<{ id: string; name: string }>> {
  return apiFetch('/units/assignable');
}

export interface UnitDashboardData {
  unit_id: string;
  unit_name: string;
  rat_status: string;
  activities_total: number;
  completeness_avg: number;
  by_status: Record<string, number>;
  riesgo: RiskIndicators;
  observaciones_abiertas: number;
  atencion: AttentionRow[];
}

export async function fetchUnitDashboard(unitId: string): Promise<UnitDashboardData> {
  return apiFetch<UnitDashboardData>(`/dashboard/unit/${unitId}`);
}

export async function transitionUnit(unitId: string, to: string): Promise<string> {
  const result = await apiFetch<{ status: string }>(`/units/${unitId}/status`, {
    method: 'POST',
    body: JSON.stringify({ to }),
  });
  return result.status;
}

// ── historial y comparacion de versiones ────────────────────────────────────

export interface VersionDiff {
  scalar: Array<{ field: string; a: unknown; b: unknown }>;
  collections: Array<{ name: string; a: number; b: number }>;
}

/**
 * Campos "de contenido" que vale la pena mostrar en un diff -- se excluyen
 * deliberadamente id/ref_code/responsible_unit_id (nunca cambian),
 * status/completeness_pct/current_version_no/updated_at (son metadata
 * derivada, no algo que el DPD "cambio" a mano) y `data_source`/`title`
 * quedan incluidos porque si son contenido real editable.
 */
const SCALAR_FIELD_LABEL: Record<string, string> = {
  title: 'Título',
  operational_owner_name: 'Responsable operativo',
  purpose: 'Finalidad',
  description: 'Descripción',
  data_source: 'Origen de los datos',
  has_international_transfer: 'Transferencia internacional',
  has_automated_decision: 'Decisiones automatizadas',
  uses_ai: 'Usa inteligencia artificial',
  own_systems_text: 'Sistemas propios',
  retention_value: 'Plazo de conservación',
  retention_unit: 'Unidad del plazo',
  retention_criterion: 'Criterio de conservación',
  retention_text: 'Notas de conservación',
};

const COLLECTION_LABEL: Record<string, string> = {
  legalBases: 'Bases de licitud',
  dataCategories: 'Categorías de datos',
  subjectCategories: 'Categorías de titulares',
  recipients: 'Destinatarios',
  transfers: 'Transferencias',
  securityMeasures: 'Medidas de seguridad',
  automatedDecisions: 'Decisiones automatizadas',
};

interface ActivitySnapshot {
  activity: Record<string, unknown>;
  [collection: string]: unknown;
}

/**
 * El calculo del diff vive ACA, del lado del cliente -- mismo patron que el
 * RPC original `diff_activity_versions`, que tambien devolvia los dos
 * snapshots crudos y dejaba el calculo campo-a-campo a la UI (ver
 * apps/workers-backend/src/versioning.ts). Compara campos escalares
 * (valor distinto) y tamaños de colección (cuantos ítems tenía cada una),
 * no el contenido item-a-item de las colecciones -- suficiente para "qué
 * cambió" sin construir un diff estructural completo.
 */
function computeVersionDiff(a: ActivitySnapshot, b: ActivitySnapshot): VersionDiff {
  const scalar: VersionDiff['scalar'] = [];
  for (const [field, label] of Object.entries(SCALAR_FIELD_LABEL)) {
    const av = a.activity?.[field];
    const bv = b.activity?.[field];
    if (JSON.stringify(av ?? null) !== JSON.stringify(bv ?? null)) {
      scalar.push({ field: label, a: av, b: bv });
    }
  }

  const collections: VersionDiff['collections'] = [];
  for (const [key, label] of Object.entries(COLLECTION_LABEL)) {
    const av = Array.isArray(a[key]) ? (a[key] as unknown[]).length : 0;
    const bv = Array.isArray(b[key]) ? (b[key] as unknown[]).length : 0;
    if (av !== bv) collections.push({ name: label, a: av, b: bv });
  }

  return { scalar, collections };
}

export async function fetchVersionDiff(activityId: string, a: number, b: number): Promise<VersionDiff> {
  const { a: snapA, b: snapB } = await apiFetch<{ a: ActivitySnapshot; b: ActivitySnapshot }>(
    `/activities/${activityId}/versions/diff?a=${a}&b=${b}`,
  );
  return computeVersionDiff(snapA, snapB);
}

export async function transitionActivity(
  activityId: string,
  to: ActivityStatus,
  comment?: string,
): Promise<ActivityStatus> {
  const result = await apiFetch<{ status: ActivityStatus }>(`/activities/${activityId}/status`, {
    method: 'POST',
    body: JSON.stringify({ to, comment }), // comment: el backend aun no lo persiste (ver README)
  });
  return result.status;
}

// ── seguimiento del levantamiento (modulo separado del RAT) ────────────────

export interface EngagementRow {
  unit_id: string;
  unit_name: string;
  stage: EngagementStage;
  contacted_on: string | null;
  meeting_on: string | null;
  pending_items: string | null;
  notes: string | null;
  contacts: number;
  activities: number;
}

export async function fetchTracking(): Promise<EngagementRow[]> {
  return apiFetch<EngagementRow[]>('/tracking');
}

export async function saveEngagement(
  unitId: string,
  patch: Partial<Pick<EngagementRow, 'stage' | 'contacted_on' | 'meeting_on' | 'pending_items' | 'notes'>>,
): Promise<void> {
  await apiFetch(`/tracking/${unitId}`, { method: 'POST', body: JSON.stringify(patch) });
}

export interface Contact {
  id: string;
  unit_id: string;
  full_name: string;
  position: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
}

export async function fetchContacts(unitId: string): Promise<Contact[]> {
  return apiFetch<Contact[]>(`/tracking/${unitId}/contacts`);
}

export async function saveContact(input: {
  unit_id: string;
  full_name: string;
  position?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}): Promise<void> {
  await apiFetch(`/tracking/${input.unit_id}/contacts`, { method: 'POST', body: JSON.stringify(input) });
}

export async function deleteContact(id: string): Promise<void> {
  await apiFetch(`/contacts/${id}/delete`, { method: 'POST' });
}

// ── catalogos administrables (src/catalog-admin.ts en el backend) ──────────

/** Mismo shape que ya usaba el asistente via @rat/shared -- code/label mas la bandera propia del catalogo, si la tiene. */
export interface PublicCatalogItem {
  code: string;
  label: string;
  [flagKey: string]: string | boolean;
}

export type CatalogName =
  | 'legal_basis'
  | 'data_category'
  | 'subject_category'
  | 'recipient_type'
  | 'security_measure'
  | 'transfer_guarantee_type'
  | 'retention_criterion';

export type PublicCatalogs = Record<CatalogName, PublicCatalogItem[]>;

/** GET /catalogs -- cualquier sesion valida, solo items activos. Usado por el asistente. */
export async function fetchCatalogs(): Promise<PublicCatalogs> {
  return apiFetch<PublicCatalogs>('/catalogs');
}

export interface AdminCatalogItem {
  id: string;
  code: string;
  label: string;
  flag: boolean;
  is_active: boolean;
  sort_order: number;
}

export type AdminCatalogs = Record<CatalogName, AdminCatalogItem[]>;

/** GET /admin/catalogs -- institucional solo, incluye items inactivos. */
export async function fetchAdminCatalogs(): Promise<AdminCatalogs> {
  return apiFetch<AdminCatalogs>('/admin/catalogs');
}

export async function createCatalogItem(input: { catalog: CatalogName; code: string; label: string; flag?: boolean }): Promise<void> {
  await apiFetch('/admin/catalogs', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateCatalogItem(
  id: string,
  patch: Partial<{ label: string; flag: boolean; is_active: boolean; sort_order: number }>,
): Promise<void> {
  await apiFetch(`/admin/catalogs/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

// ── consola de administracion de usuarios (src/admin.ts en el backend) ─────

export type UnitRole = 'jefe' | 'colaborador' | 'consulta';

export interface AdminUser {
  id: string;
  email: string;
  is_institutional: boolean;
  is_auditor: boolean;
  mfa_enabled: boolean;
  units: Array<{ unit_id: string; unit_name: string; unit_role: UnitRole }>;
}

/** GET /admin/users -- institucional solo. */
export async function fetchAdminUsers(): Promise<AdminUser[]> {
  return apiFetch<AdminUser[]>('/admin/users');
}

export async function createUserApi(input: {
  email: string;
  password: string;
  is_institutional?: boolean;
  is_auditor?: boolean;
}): Promise<void> {
  await apiFetch('/admin/users', { method: 'POST', body: JSON.stringify(input) });
}

/** Solo is_institutional/is_auditor -- no hay endpoint para cambiar el email. */
export async function updateUserRoles(id: string, patch: Partial<{ is_institutional: boolean; is_auditor: boolean }>): Promise<void> {
  await apiFetch(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function assignUserToUnitApi(userId: string, unitId: string, unitRole: UnitRole): Promise<void> {
  await apiFetch(`/admin/users/${userId}/units`, { method: 'POST', body: JSON.stringify({ unit_id: unitId, unit_role: unitRole }) });
}

export async function removeUserFromUnitApi(userId: string, unitId: string): Promise<void> {
  await apiFetch(`/admin/users/${userId}/units/${unitId}/remove`, { method: 'POST' });
}

export async function resetUserPasswordApi(userId: string, password: string): Promise<void> {
  await apiFetch(`/admin/users/${userId}/reset-password`, { method: 'POST', body: JSON.stringify({ password }) });
}

// ── auditoria (src/audit.ts en el backend) ──────────────────────────────────

export interface AuditEntry {
  id: number;
  occurred_at: string;
  actor_user_id: string | null;
  actor_email: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  previous_state: string | null;
  new_state: string | null;
}

/** GET /audit -- institucional solo. `beforeId` para "cargar más" (cursor, no offset). */
export async function fetchAuditEntries(opts: { limit?: number; beforeId?: number; action?: string; entityType?: string } = {}): Promise<AuditEntry[]> {
  const params = new URLSearchParams();
  if (opts.limit) params.set('limit', String(opts.limit));
  if (opts.beforeId) params.set('before_id', String(opts.beforeId));
  if (opts.action) params.set('action', opts.action);
  if (opts.entityType) params.set('entity_type', opts.entityType);
  const qs = params.toString();
  return apiFetch<AuditEntry[]>(`/audit${qs ? `?${qs}` : ''}`);
}

export interface AuditChainResult {
  ok: boolean;
  brokenAtId: number | null;
  rowsChecked: number;
}

/** GET /audit/verify -- institucional solo. */
export async function fetchAuditVerify(): Promise<AuditChainResult> {
  return apiFetch<AuditChainResult>('/audit/verify');
}

// ── importacion asistida con staging y curacion (src/import.ts) ────────────

export type ImportKind = 'activity_seed' | 'contact';

export type ImportRowError = 'missing_title' | 'missing_full_name' | 'missing_unit' | 'forbidden';

export interface ImportRowResult {
  row: number;
  ok: boolean;
  id?: string;
  ref_code?: string;
  error?: ImportRowError;
}

export interface ImportActivityRowInput {
  unit_id: string;
  title: string;
  description?: string;
  operational_owner_name?: string;
}

export interface ImportContactRowInput {
  unit_id: string;
  full_name: string;
  position?: string;
  email?: string;
  phone?: string;
  notes?: string;
}

/**
 * El mapeo de columnas, el parseo del archivo y la resolucion de unidad ya
 * ocurrieron en el cliente -- ver lib/importApi.ts y pages/Import.tsx.
 * `columnMapping` (encabezado de origen -> campo IMPORT_FIELDS, sin incluir
 * "unidad", que se resuelve aparte) viaja junto al lote para que el backend
 * la "aprenda" solo si el lote crea algo -- ver getColumnMappings() en
 * src/import.ts.
 */
export async function importRowsApi(
  kind: ImportKind,
  rows: ImportActivityRowInput[] | ImportContactRowInput[],
  columnMapping?: Record<string, string>,
): Promise<ImportRowResult[]> {
  const result = await apiFetch<{ results: ImportRowResult[] }>('/activities/import', {
    method: 'POST',
    body: JSON.stringify({ kind, rows, column_mapping: columnMapping }),
  });
  return result.results;
}

/** Correspondencia de columnas aprendida de lotes anteriores (compartida entre usuarios de la institucion). */
export async function fetchColumnMappings(kind: ImportKind): Promise<Record<string, string>> {
  const result = await apiFetch<{ mapping: Record<string, string> }>(`/activities/import/column-mappings?kind=${kind}`);
  return result.mapping;
}

// ── adjuntos de actividad (Cloudflare R2, src/attachments.ts) ──────────────

export interface AttachmentRow {
  id: string;
  filename: string;
  mime: string | null;
  size_bytes: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export async function fetchAttachments(activityId: string): Promise<AttachmentRow[]> {
  const result = await apiFetch<{ attachments: AttachmentRow[] }>(`/activities/${activityId}/attachments`);
  return result.attachments;
}

export async function uploadAttachmentApi(activityId: string, file: File): Promise<{ id: string }> {
  const form = new FormData();
  form.set('file', file);
  return apiUpload<{ ok: true; id: string }>(`/activities/${activityId}/attachments`, form);
}

export async function deleteAttachmentApi(activityId: string, attachmentId: string): Promise<void> {
  await apiFetch(`/activities/${activityId}/attachments/${attachmentId}`, { method: 'DELETE' });
}

/** Dispara la descarga del navegador -- el archivo nunca tiene URL publica, siempre pasa por fetch() con el token. */
export async function downloadAttachment(activityId: string, attachmentId: string, fallbackFilename: string): Promise<void> {
  const { blob, filename } = await apiDownload(`/activities/${activityId}/attachments/${attachmentId}`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? fallbackFilename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ── bandeja y tablero del revisor (Fase 5) ─────────────────────────────────

export type ReviewPriority = 0 | 1 | 2 | 3;

/** Una tarea de revision abierta (GET /review/queue): vencidas primero, luego prioridad, luego la mas antigua. */
export interface ReviewQueueRow {
  task_id: string;
  activity_id: string;
  ref_code: string | null;
  title: string;
  unit_id: string;
  unit_name: string;
  cycle_no: number;
  priority: ReviewPriority;
  assignee_id: string | null;
  assignee_email: string | null;
  opened_at: string;
  due_at: string;
  age_hours: number;
  hours_to_due: number;
  overdue: boolean;
}

export interface ReviewerDashboardData {
  resumen: {
    mias: number;
    sin_asignar: number;
    atrasadas: number;
    vencen_24h: number;
    por_prioridad: Record<'0' | '1' | '2' | '3', number>;
  };
  cola: ReviewQueueRow[];
  esperando_a_la_unidad: Array<{ activity_id: string; ref_code: string | null; title: string; unit_name: string; dias: number }>;
  por_confirmar: Array<{
    observation_id: string;
    activity_id: string;
    ref_code: string | null;
    field_path: string;
    severity: string;
    respondida_hace_horas: number;
  }>;
}

export type ReviewQueueFilter = 'todas' | 'mias' | 'sin_asignar';

export async function fetchReviewerDashboard(): Promise<ReviewerDashboardData> {
  return apiFetch<ReviewerDashboardData>('/dashboards/revisor');
}

export async function fetchReviewQueue(filter: ReviewQueueFilter = 'todas'): Promise<ReviewQueueRow[]> {
  const qs = filter === 'mias' ? '?mine=1' : filter === 'sin_asignar' ? '?unassigned=1' : '';
  return apiFetch<ReviewQueueRow[]>(`/review/queue${qs}`);
}

/** El revisor toma una tarea libre (dos revisores no pueden tomar la misma: el segundo recibe `already_assigned`). */
export async function claimReviewTask(taskId: string): Promise<void> {
  await apiFetch(`/review/tasks/${taskId}/claim`, { method: 'POST' });
}

export async function releaseReviewTask(taskId: string): Promise<void> {
  await apiFetch(`/review/tasks/${taskId}/release`, { method: 'POST' });
}

// ── consola de roles (GET|POST /admin/role-assignments, GET /admin/roles; Fase 5) ─────────

export type RoleScopeType = 'global' | 'unit_type' | 'unit';

/** Lo que hace falta para armar el formulario sin adivinar que combinaciones rol/alcance son validas. */
export interface RoleCatalogData {
  roles: Array<{ role: RatRole; permissions: string[]; allowed_scopes: RoleScopeType[] }>;
  scope_types: RoleScopeType[];
  /** Categorias de unidad que existen hoy (las que sirven de alcance `unit_type`). */
  unit_types: string[];
}

export interface RoleAssignmentRow {
  id: string;
  user_id: string;
  email: string | null;
  role: RatRole;
  scope_type: RoleScopeType;
  scope_id: string | null;
  /** Nombre de la unidad (alcance 'unit') o codigo de categoria ('unit_type'); null si es global. */
  scope_label: string | null;
  include_descendants: boolean;
  granted_by: string;
  granted_at: string;
}

export interface GrantRoleInput {
  user_id: string;
  role: RatRole;
  scope_type: RoleScopeType;
  scope_id: string | null;
  include_descendants?: boolean;
}

export async function fetchRoleCatalog(): Promise<RoleCatalogData> {
  return apiFetch<RoleCatalogData>('/admin/roles');
}

export async function fetchRoleAssignments(): Promise<RoleAssignmentRow[]> {
  return apiFetch<RoleAssignmentRow[]>('/admin/role-assignments');
}

export async function grantRoleApi(input: GrantRoleInput): Promise<void> {
  await apiFetch('/admin/role-assignments', { method: 'POST', body: JSON.stringify(input) });
}

/** POST y no DELETE: el CORS del backend solo permite GET, POST y PATCH. */
export async function revokeRoleApi(assignmentId: string): Promise<void> {
  await apiFetch(`/admin/role-assignments/${assignmentId}/revoke`, { method: 'POST' });
}

// ── tablero del jefe de unidad (GET /dashboards/jefe; Fase 5) ──────────────────────────────

/** Una observacion que la unidad aun no responde (estado 'abierta' o 'reabierta'). */
export interface PendingObservation {
  observation_id: string;
  activity_id: string;
  unit_id: string;
  ref_code: string | null;
  title: string;
  field_path: string;
  severity: ObservationSeverity;
  state: 'abierta' | 'reabierta';
  /** Texto de la observacion. Opcional solo por el desfase entre desplegar el frontend y el backend que lo agrega. */
  text?: string;
  created_at?: string;
}

export interface JefeDashboardData {
  unidades: Array<{
    unit_id: string;
    name: string;
    rat_status: string;
    avance_pct: number;
    por_estado: Record<string, number>;
    /** Que le falta para poder pasar a "Completada" (null = nada la bloquea). */
    bloqueos_para_completar: GateInfo | null;
  }>;
  responder: PendingObservation[];
  borradores: Array<{ activity_id: string; ref_code: string | null; title: string; completeness_pct: number }>;
}

export async function fetchJefeDashboard(): Promise<JefeDashboardData> {
  return apiFetch<JefeDashboardData>('/dashboards/jefe');
}

// ── tableros del auditor y del administrador (GET /dashboards/auditor y /dashboards/admin; Fase 5) ──────

export interface AuditorDashboardData {
  /** Ultima verificacion GUARDADA (la escribe el proceso programado, no `GET /audit/verify`); null = aun no hay. */
  integridad: { ok: boolean | null; verificado_en: string | null; eventos: number | null };
  transiciones_30d: { total: number; por_destino: Record<string, number>; reaperturas: number };
  actividad_por_dia: Array<{ dia: string; eventos: number }>;
  top_actores: Array<{ email: string | null; cambios: number }>;
  controles_detectivos: { aprobadas_sin_transicion: number; autoaprobaciones_90d: number; cambios_fuera_de_horario_30d: number };
  /** Una fila por unidad dentro del alcance del auditor. */
  cumplimiento: { unidades: ExecUnit[] };
}

export interface AdminDashboardData {
  generated_at: string;
  totales: { unidades: number; actividades: number; completitud_pct: number };
  actividades_por_estado: Record<string, number>;
  unidades_por_estado: Record<string, number>;
  revision: { abiertas: number; sin_asignar: number; atrasadas: number; vencen_24h: number; edad_promedio_horas: number };
  desempeno_30d: { cerradas: number; en_plazo_pct: number | null; ciclo_promedio_horas: number | null; rondas_promedio_hasta_aprobar: number | null };
  carga_revisores: Array<{ user_id: string; email: string; abiertas: number; atrasadas: number }>;
  unidades_estancadas: Array<{ unit_id: string; name: string; dias_sin_movimiento: number | null }>;
  /** Tareas abiertas vencidas o que vencen en menos de 24 h, la mas urgente primero (max. 10; el total es atrasadas + vencen_24h). */
  tareas_sla_criticas: SlaTask[];
}

export interface SlaTask {
  task_id: string;
  activity_id: string;
  ref_code: string | null;
  title: string;
  unit_name: string;
  assignee_email: string | null;
  priority: number;
  due_at: string;
  vencida: boolean;
  /** Redondeadas y solo para mostrar (negativo = ya vencio); para saber si vencio, `vencida`. */
  horas_restantes: number;
}

export async function fetchAuditorDashboard(): Promise<AuditorDashboardData> {
  return apiFetch<AuditorDashboardData>('/dashboards/auditor');
}

export async function fetchAdminDashboard(): Promise<AdminDashboardData> {
  return apiFetch<AdminDashboardData>('/dashboards/admin');
}

// ── notificaciones in-app (GET /notifications, POST /notifications/read; Fase 5, patron outbox) ──────────
// El evento se guarda en el outbox junto con el cambio de negocio y un proceso programado (cada 5 min) lo reparte a
// `notifications`: una novedad puede tardar unos minutos en aparecer. Varias del mismo tipo sobre la misma entidad
// se colapsan en una fila con `count`.

export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  /** Ruta interna de la app (p. ej. /actividades/<id>) o null. */
  link: string | null;
  urgency: 'normal' | 'alta';
  count: number;
  read_at: string | null;
  updated_at: string;
}

export interface NotificationsData {
  /** Total de no leidas (puede ser mayor que `items`, que trae como maximo 50). */
  unread: number;
  items: NotificationItem[];
}

export async function fetchNotifications(): Promise<NotificationsData> {
  return apiFetch<NotificationsData>('/notifications');
}

/** Sin `ids`, marca todas las propias como leidas. */
export async function markNotificationsRead(ids?: string[]): Promise<void> {
  await apiFetch('/notifications/read', { method: 'POST', body: JSON.stringify(ids ? { ids } : {}) });
}

/**
 * Vuelve a dejar sin leer UNA notificacion. `changed: false` si ya hay otra sin leer del mismo tema (llego una novedad
 * nueva): esa ya hace de recordatorio y la base admite una sola abierta por tema.
 */
export async function markNotificationUnread(id: string): Promise<{ changed: boolean }> {
  return apiFetch<{ ok: true; changed: boolean }>('/notifications/unread', { method: 'POST', body: JSON.stringify({ id }) });
}

/** El administrador (`review.assign`) asigna la tarea a otra persona; el backend valida que pueda revisar esa unidad. */
export async function assignReviewTask(taskId: string, assigneeId: string): Promise<void> {
  await apiFetch(`/review/tasks/${taskId}/assign`, { method: 'POST', body: JSON.stringify({ assignee_id: assigneeId }) });
}

/** Cambia la prioridad y, con ella, el plazo (se recalcula desde la apertura de la tarea). */
export async function setReviewPriority(taskId: string, priority: ReviewPriority): Promise<void> {
  await apiFetch(`/review/tasks/${taskId}/priority`, { method: 'POST', body: JSON.stringify({ priority }) });
}
